import { cn } from "@/lib/utils";
import { MediaAsset } from "@/types/media";
import { Copy, ImageIcon, Trash2 } from "lucide-react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Checkbox } from "../ui/checkbox";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";

export type MediaCardProps = {
  asset: MediaAsset;
  selected?: boolean;
  onToggleSelect?: () => void;
  onPreview?: () => void;
  onCopyUrl?: () => void;
  onDelete?: () => void;
};

export function MediaCard({
  asset,
  selected,
  onToggleSelect,
  onPreview,
  onCopyUrl,
  onDelete,
}: MediaCardProps) {
  const imageUrl = asset.fileUrl || asset.s3Url || "";
  const sizeKB = asset.fileSize ? Math.max(1, Math.round(asset.fileSize / 1024)) : 0;

  return (
    <Card
      className={cn(
        "overflow-hidden border-slate-200 transition-shadow hover:shadow-md w-full cursor-pointer",
        selected && "ring-2 ring-blue-400"
      )}
      onClick={onPreview}
    >
      {/* Thumbnail */}
      <div className="relative aspect-square bg-slate-50">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={asset.fileName}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover transition duration-300 ease-in-out blur-sm"
            onLoad={(e) => e.currentTarget.classList.remove("blur-sm")}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-slate-400">
            <ImageIcon className="h-6 w-6" />
          </div>
        )}

        {/* Selection checkbox — top-left */}
        {onToggleSelect && (
          <div
            className="absolute left-1.5 top-1.5 z-10 rounded bg-white/90 p-0.5 shadow-sm"
            onClick={(e) => e.stopPropagation()}
          >
            <Checkbox
              checked={!!selected}
              onCheckedChange={onToggleSelect}
              className="h-3.5 w-3.5"
            />
          </div>
        )}

        {/* Action buttons — top-right, always visible */}
        <div
          className="absolute right-1.5 top-1.5 z-10 flex gap-1"
          onClick={(e) => e.stopPropagation()}
        >
          {onCopyUrl && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant="secondary"
                  className="h-6 w-6 bg-white/95 shadow-sm"
                  onClick={onCopyUrl}
                >
                  <Copy className="h-3 w-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Copy URL</TooltipContent>
            </Tooltip>
          )}
          {onDelete && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant="secondary"
                  className="h-6 w-6 bg-white/95 shadow-sm text-rose-600 hover:text-rose-700"
                  onClick={onDelete}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Delete</TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Meta */}
      <CardContent className="p-2">
        <p
          className="truncate text-xs font-medium text-slate-900 leading-tight"
          title={asset.fileName}
        >
          {asset.fileName}
        </p>
        <div className="mt-0.5 flex items-center justify-between text-[10px] text-slate-500">
          <span className="truncate max-w-[70%]">{asset.fileType || "unknown"}</span>
          <span className="shrink-0">{sizeKB} KB</span>
        </div>
        {asset.createdAt && (
          <div className="mt-1 hidden sm:block">
            <Badge variant="outline" className="text-[10px] px-1 py-0">
              {new Date(asset.createdAt).toLocaleDateString()}
            </Badge>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
